/**
 * Wikitravel URL Validation Service
 * 
 * This service validates Wikitravel URLs to ensure they are accessible before
 * displaying them to users. Many Wikitravel URLs return "Forbidden" errors,
 * so this service checks accessibility and caches results for performance.
 * 
 * Features:
 * - HEAD request validation to check URL accessibility
 * - Caching system to avoid repeated validation calls
 * - Batch validation for multiple URLs
 * - Error handling and fallback mechanisms
 * - Configurable timeout and retry logic
 */

/**
 * Cache for storing validation results
 * Structure: { url: { isValid: boolean, timestamp: number, expiresAt: number } }
 */
const validationCache = new Map();

/**
 * Cache configuration
 */
const CACHE_CONFIG = {
  TTL: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
  MAX_CACHE_SIZE: 1000, // Maximum number of cached entries
  CLEANUP_INTERVAL: 60 * 60 * 1000 // Cleanup every hour
};

/**
 * Request configuration
 */
const REQUEST_CONFIG = {
  TIMEOUT: 5000, // 5 seconds timeout
  RETRY_ATTEMPTS: 2,
  RETRY_DELAY: 1000 // 1 second delay between retries
};

/**
 * Validates a single Wikitravel URL by making a HEAD request
 * @param {string} url - The URL to validate
 * @returns {Promise<boolean>} True if URL is accessible, false otherwise
 */
async function validateSingleUrl(url) {
  try {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_CONFIG.TIMEOUT);

    const response = await fetch(url, {
      method: 'HEAD',
      signal: controller.signal,
      headers: {
        'User-Agent': 'Mozilla/5.0 (compatible; TripPlanner/1.0)',
        'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
      }
    });

    clearTimeout(timeoutId);

    // Consider 200-299 status codes as valid
    const isValid = response.status >= 200 && response.status < 300;
    
    console.log(`🔍 [WikitravelValidation] URL validation result: ${url} - Status: ${response.status}, Valid: ${isValid}`);
    
    return isValid;
  } catch (error) {
    console.warn(`⚠️ [WikitravelValidation] Error validating URL ${url}:`, error.message);
    return false;
  }
}

/**
 * Validates a URL with retry logic
 * @param {string} url - The URL to validate
 * @returns {Promise<boolean>} True if URL is accessible, false otherwise
 */
async function validateUrlWithRetry(url) {
  let lastError;
  
  for (let attempt = 1; attempt <= REQUEST_CONFIG.RETRY_ATTEMPTS; attempt++) {
    try {
      const isValid = await validateSingleUrl(url);
      return isValid;
    } catch (error) {
      lastError = error;
      console.warn(`⚠️ [WikitravelValidation] Attempt ${attempt} failed for ${url}:`, error.message);
      
      if (attempt < REQUEST_CONFIG.RETRY_ATTEMPTS) {
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, REQUEST_CONFIG.RETRY_DELAY));
      }
    }
  }
  
  console.error(`❌ [WikitravelValidation] All attempts failed for ${url}:`, lastError.message);
  return false;
}

/**
 * Checks if a cached validation result is still valid
 * @param {string} url - The URL to check
 * @returns {Object|null} Cached result if valid, null otherwise
 */
function getCachedResult(url) {
  const cached = validationCache.get(url);
  
  if (!cached) {
    return null;
  }
  
  // Check if cache entry has expired
  if (Date.now() > cached.expiresAt) {
    validationCache.delete(url);
    return null;
  }
  
  return cached;
}

/**
 * Stores validation result in cache
 * @param {string} url - The URL that was validated
 * @param {boolean} isValid - Whether the URL is accessible
 */
function setCachedResult(url, isValid) {
  const now = Date.now();
  const expiresAt = now + CACHE_CONFIG.TTL;
  
  validationCache.set(url, {
    isValid,
    timestamp: now,
    expiresAt
  });
  
  // Cleanup cache if it gets too large
  if (validationCache.size > CACHE_CONFIG.MAX_CACHE_SIZE) {
    cleanupCache();
  }
}

/**
 * Cleans up expired cache entries
 */
function cleanupCache() {
  const now = Date.now();
  let cleanedCount = 0;
  
  for (const [url, cached] of validationCache.entries()) {
    if (now > cached.expiresAt) {
      validationCache.delete(url);
      cleanedCount++;
    }
  }
  
  if (cleanedCount > 0) {
    console.log(`🧹 [WikitravelValidation] Cleaned up ${cleanedCount} expired cache entries`);
  }
}

/**
 * Generates Wikitravel URL for a place name
 * @param {string} placeName - The name of the place
 * @returns {string} The Wikitravel URL
 */
function generateWikitravelUrl(placeName) {
  return `https://wikitravel.org/en/${encodeURIComponent(placeName)}`;
}

/**
 * Main validation function - validates Wikitravel URL for a place
 * @param {string} placeName - The name of the place
 * @returns {Promise<boolean>} True if Wikitravel page exists and is accessible
 */
export async function validateWikitravelUrl(placeName) {
  if (!placeName || typeof placeName !== 'string') {
    console.warn('⚠️ [WikitravelValidation] Invalid place name provided');
    return false;
  }
  
  const url = generateWikitravelUrl(placeName);
  
  // Check cache first
  const cached = getCachedResult(url);
  if (cached !== null) {
    console.log(`📋 [WikitravelValidation] Using cached result for ${placeName}: ${cached.isValid}`);
    return cached.isValid;
  }
  
  // Validate URL
  console.log(`🔍 [WikitravelValidation] Validating Wikitravel URL for: ${placeName}`);
  const isValid = await validateUrlWithRetry(url);
  
  // Cache the result
  setCachedResult(url, isValid);
  
  return isValid;
}

/**
 * Validates multiple Wikitravel URLs in parallel
 * @param {string[]} placeNames - Array of place names to validate
 * @returns {Promise<Object>} Object mapping place names to validation results
 */
export async function batchValidateWikitravelUrls(placeNames) {
  if (!Array.isArray(placeNames) || placeNames.length === 0) {
    console.warn('⚠️ [WikitravelValidation] Invalid place names array provided');
    return {};
  }
  
  console.log(`🔍 [WikitravelValidation] Batch validating ${placeNames.length} Wikitravel URLs`);
  
  // Create validation promises for all place names
  const validationPromises = placeNames.map(async (placeName) => {
    const isValid = await validateWikitravelUrl(placeName);
    return { placeName, isValid };
  });
  
  // Wait for all validations to complete
  const results = await Promise.all(validationPromises);
  
  // Convert to object mapping
  const resultMap = {};
  results.forEach(({ placeName, isValid }) => {
    resultMap[placeName] = isValid;
  });
  
  console.log(`✅ [WikitravelValidation] Batch validation completed. Results:`, resultMap);
  
  return resultMap;
}

/**
 * Gets validation statistics for monitoring
 * @returns {Object} Statistics about validation cache and performance
 */
export function getValidationStats() {
  const now = Date.now();
  let validCount = 0;
  let invalidCount = 0;
  let expiredCount = 0;
  
  for (const [url, cached] of validationCache.entries()) {
    if (now > cached.expiresAt) {
      expiredCount++;
    } else if (cached.isValid) {
      validCount++;
    } else {
      invalidCount++;
    }
  }
  
  return {
    totalCached: validationCache.size,
    validUrls: validCount,
    invalidUrls: invalidCount,
    expiredEntries: expiredCount,
    cacheHitRate: validationCache.size > 0 ? ((validCount + invalidCount) / validationCache.size * 100).toFixed(2) + '%' : '0%'
  };
}

/**
 * Clears the validation cache
 */
export function clearValidationCache() {
  const size = validationCache.size;
  validationCache.clear();
  console.log(`🧹 [WikitravelValidation] Cleared validation cache (${size} entries)`);
}

/**
 * Preloads validation for common place names
 * This can be called during app initialization to warm up the cache
 * @param {string[]} commonPlaceNames - Array of common place names to preload
 */
export async function preloadCommonPlaces(commonPlaceNames = []) {
  if (!Array.isArray(commonPlaceNames) || commonPlaceNames.length === 0) {
    console.log('📋 [WikitravelValidation] No common places to preload');
    return;
  }
  
  console.log(`🚀 [WikitravelValidation] Preloading validation for ${commonPlaceNames.length} common places`);
  
  try {
    await batchValidateWikitravelUrls(commonPlaceNames);
    console.log('✅ [WikitravelValidation] Preloading completed');
  } catch (error) {
    console.error('❌ [WikitravelValidation] Error during preloading:', error);
  }
}

// Set up periodic cache cleanup
setInterval(cleanupCache, CACHE_CONFIG.CLEANUP_INTERVAL);

// Export the service object
export const wikitravelValidationService = {
  validateWikitravelUrl,
  batchValidateWikitravelUrls,
  getValidationStats,
  clearValidationCache,
  preloadCommonPlaces,
  generateWikitravelUrl
};

export default wikitravelValidationService;
